import ClickAwayListener from "@mui/material/ClickAwayListener";
import {
	AlignCenter,
	AlignJustify,
	AlignLeft,
	AlignRight,
	Baseline,
	Bold,
	ChevronDown,
	Code,
	CodeXml,
	Heading1,
	Heading2,
	Heading3,
	Heading4,
	ImagePlus,
	Italic,
	Link as LinkIcon,
	List,
	ListOrdered,
	ListTodo,
	PaintBucket,
	PanelTop,
	Pilcrow,
	Quote,
	Redo2,
	Strikethrough,
	Table as TableIcon,
	Underline as UnderlineIcon,
	Undo2,
} from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ScrollShadowWrapper } from "src/components/Default/LekUI/ScrollShadowWrapper";
import { UploadModal } from "src/components/Modals/UploadModal";
import { blobUrlToCompressedFile } from "src/helpers/blobUrlToCompressedFile";
import newApi from "src/newApi";
import {
	BG_COLORS,
	ColorPickerDropdown,
	TEXT_COLORS,
} from "./ColorPickerDropdown";
import { DropdownPortal } from "./DropdownPortal";
import { CALLOUT_TYPES } from "./extensions/CalloutExtension";
import { getTableFigureParts } from "./extensions/TableFigureExtension";
import TipTapLinkModal from "./TipTapLinkModal";

function ToolbarButton({ onClick, isActive, disabled, title, children }) {
	return (
		<button
			type="button"
			className={`tiptap-toolbar__btn${isActive ? " tiptap-toolbar__btn--active" : ""}`}
			onClick={onClick}
			disabled={disabled}
			title={title}
		>
			{children}
		</button>
	);
}

function ToolbarSeparator() {
	return <div className="tiptap-toolbar__separator" />;
}

function HeadingDropdown({ editor }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const btnRef = useRef(null);

	const currentLevel = editor.getAttributes("heading")?.level;

	const items = [
		{
			level: 0,
			icon: <Pilcrow size={18} />,
			label: t("editor.normalText", "Normal text"),
		},
		{
			level: 1,
			icon: <Heading1 size={18} />,
			label: t("editor.heading1", "Heading 1"),
		},
		{
			level: 2,
			icon: <Heading2 size={18} />,
			label: t("editor.heading2", "Heading 2"),
		},
		{
			level: 3,
			icon: <Heading3 size={18} />,
			label: t("editor.heading3", "Heading 3"),
		},
		{
			level: 4,
			icon: <Heading4 size={18} />,
			label: t("editor.heading4", "Heading 4"),
		},
	];

	const currentHeadingItem =
		items.find((item) => item.level === currentLevel) || items[0];

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<div className="tiptap-toolbar__dropdown-wrapper">
				<button
					ref={btnRef}
					type="button"
					className={`tiptap-toolbar__btn tiptap-toolbar__btn--dropdown${currentLevel ? " tiptap-toolbar__btn--active" : ""}`}
					onClick={() => setOpen(!open)}
					title="Heading"
				>
					{currentHeadingItem.icon}
					<ChevronDown size={12} />
				</button>
				<DropdownPortal anchorRef={btnRef} open={open}>
					{items.map((item) => (
						<button
							key={item.level}
							type="button"
							className={`tiptap-toolbar__dropdown-item${editor.isActive("heading", { level: item.level }) ? " tiptap-toolbar__dropdown-item--active" : ""}`}
							onClick={() => {
								editor
									.chain()
									.focus()
									.toggleHeading({ level: item.level })
									.run();
								setOpen(false);
							}}
						>
							{item.icon}
							<span>{item.label}</span>
						</button>
					))}
				</DropdownPortal>
			</div>
		</ClickAwayListener>
	);
}

function ListDropdown({ editor }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const btnRef = useRef(null);
	const isAnyListActive =
		editor.isActive("bulletList") ||
		editor.isActive("orderedList") ||
		editor.isActive("taskList");

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<div className="tiptap-toolbar__dropdown-wrapper">
				<button
					ref={btnRef}
					type="button"
					className={`tiptap-toolbar__btn tiptap-toolbar__btn--dropdown${isAnyListActive ? " tiptap-toolbar__btn--active" : ""}`}
					onClick={() => setOpen(!open)}
					title="Lists"
				>
					<List size={18} />
					<ChevronDown size={12} />
				</button>
				<DropdownPortal anchorRef={btnRef} open={open}>
					<button
						type="button"
						className={`tiptap-toolbar__dropdown-item${editor.isActive("bulletList") ? " tiptap-toolbar__dropdown-item--active" : ""}`}
						onClick={() => {
							editor.chain().focus().toggleBulletList().run();
							setOpen(false);
						}}
					>
						<List size={18} />
						<span>{t("editor.bulletList", "Bullet List")}</span>
					</button>
					<button
						type="button"
						className={`tiptap-toolbar__dropdown-item${editor.isActive("orderedList") ? " tiptap-toolbar__dropdown-item--active" : ""}`}
						onClick={() => {
							editor.chain().focus().toggleOrderedList().run();
							setOpen(false);
						}}
					>
						<ListOrdered size={18} />
						<span>{t("editor.orderedList", "Ordered List")}</span>
					</button>
					<button
						type="button"
						className={`tiptap-toolbar__dropdown-item${editor.isActive("taskList") ? " tiptap-toolbar__dropdown-item--active" : ""}`}
						onClick={() => {
							editor.chain().focus().toggleTaskList().run();
							setOpen(false);
						}}
					>
						<ListTodo size={18} />
						<span>{t("editor.taskList", "Task List")}</span>
					</button>
				</DropdownPortal>
			</div>
		</ClickAwayListener>
	);
}

function AlignDropdown({ editor }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const btnRef = useRef(null);
	const textAlign = editor.getAttributes("textStyle")?.textAlign || "left";

	const currentIcon = () => {
		switch (textAlign) {
			case "center":
				return <AlignCenter size={18} />;
			case "right":
				return <AlignRight size={18} />;
			case "justify":
				return <AlignJustify size={18} />;
			default:
				return <AlignLeft size={18} />;
		}
	};

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<div className="tiptap-toolbar__dropdown-wrapper">
				<button
					ref={btnRef}
					type="button"
					className="tiptap-toolbar__btn tiptap-toolbar__btn--dropdown"
					onClick={() => setOpen(!open)}
					title="Alignment"
				>
					{currentIcon()}
					<ChevronDown size={12} />
				</button>
				<DropdownPortal anchorRef={btnRef} open={open}>
					<button
						type="button"
						className={`tiptap-toolbar__dropdown-item${editor.isActive({ textAlign: "left" }) ? " tiptap-toolbar__dropdown-item--active" : ""}`}
						onClick={() => {
							editor.chain().focus().setTextAlign("left").run();
							setOpen(false);
						}}
					>
						<AlignLeft size={18} />
						<span>{t("editor.alignLeft", "Do lewej")}</span>
					</button>
					<button
						type="button"
						className={`tiptap-toolbar__dropdown-item${editor.isActive({ textAlign: "center" }) ? " tiptap-toolbar__dropdown-item--active" : ""}`}
						onClick={() => {
							editor.chain().focus().setTextAlign("center").run();
							setOpen(false);
						}}
					>
						<AlignCenter size={18} />
						<span>{t("editor.alignCenter", "Do środka")}</span>
					</button>
					<button
						type="button"
						className={`tiptap-toolbar__dropdown-item${editor.isActive({ textAlign: "right" }) ? " tiptap-toolbar__dropdown-item--active" : ""}`}
						onClick={() => {
							editor.chain().focus().setTextAlign("right").run();
							setOpen(false);
						}}
					>
						<AlignRight size={18} />
						<span>{t("editor.alignRight", "Do prawej")}</span>
					</button>
					<button
						type="button"
						className={`tiptap-toolbar__dropdown-item${editor.isActive({ textAlign: "justify" }) ? " tiptap-toolbar__dropdown-item--active" : ""}`}
						onClick={() => {
							editor.chain().focus().setTextAlign("justify").run();
							setOpen(false);
						}}
					>
						<AlignJustify size={18} />
						<span>{t("editor.alignJustify", "Wyjustuj")}</span>
					</button>
				</DropdownPortal>
			</div>
		</ClickAwayListener>
	);
}

// Wszystkie funkcje paska narzędzi (domyślnie pełny edytor).
export const ALL_TOOLBAR_FEATURES = [
	"history",
	"headings",
	"lists",
	"blockquote",
	"codeBlock",
	"bold",
	"italic",
	"strike",
	"code",
	"underline",
	"link",
	"textColor",
	"bgColor",
	"align",
	"image",
	"callout",
];

// Okrojony zestaw używany w edytorze fiszek.
export const FLASHCARDS_TOOLBAR_FEATURES = [
	"lists",
	"blockquote",
	"bold",
	"italic",
	"strike",
	"textColor",
	"image",
];

export default function TipTapToolbar({
	editor,
	features = ALL_TOOLBAR_FEATURES,
	inlineFirst = false,
	label = undefined,
	uploadImagesOnInsert = false,
	actions = undefined,
}) {
	const { t } = useTranslation();
	const [showLinkModal, setShowLinkModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);

	if (!editor) return null;

	const has = (feature) => features.includes(feature);

	const handleImageConfirm = async (file) => {
		const blobUrl = URL.createObjectURL(file.file);
		if (!uploadImagesOnInsert) {
			editor.chain().focus().setImage({ src: blobUrl, alt: file.name }).run();
			return;
		}

		// od razu na serwer — w HTML nie zostaje blob:, wiec szkic w localStorage przezyje odswiezenie
		try {
			const result = await newApi.uploadImage(
				await blobUrlToCompressedFile(blobUrl),
			);
			if (!result?.url) throw new Error("Image upload failed");
			editor
				.chain()
				.focus()
				.setImage({ src: result.url, alt: file.name })
				.run();
		} catch {
			toast.error(t("editor.uploadError", "Nie udało się dodać zdjęcia"), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 5000,
			});
		} finally {
			URL.revokeObjectURL(blobUrl);
		}
	};

	// Każda sekcja jest oddzielona separatorem; jedna sekcja może zawierać
	// wiele grup przycisków. Sekcje budujemy warunkowo wg `features`.
	let historySection = null;
	let blockSection = null;
	let inlineSectionFrag = null;
	let mediaSectionFrag = null;

	if (has("history")) {
		historySection = (
			<div className="tiptap-toolbar__group" key="history">
				<ToolbarButton
					onClick={() => editor.chain().focus().undo().run()}
					title="Undo"
				>
					<Undo2 size={18} />
				</ToolbarButton>
				<ToolbarButton
					onClick={() => editor.chain().focus().redo().run()}
					title="Redo"
				>
					<Redo2 size={18} />
				</ToolbarButton>
			</div>
		);
	}

	const blockGroup = [];
	if (has("headings")) {
		blockGroup.push(<HeadingDropdown editor={editor} key="heading" />);
	}
	if (has("lists")) {
		blockGroup.push(<ListDropdown editor={editor} key="list" />);
	}
	if (has("blockquote")) {
		blockGroup.push(
			<ToolbarButton
				key="blockquote"
				onClick={() => editor.chain().focus().toggleBlockquote().run()}
				isActive={editor.isActive("blockquote")}
				title="Blockquote"
			>
				<Quote size={18} />
			</ToolbarButton>,
		);
	}
	if (has("codeBlock")) {
		blockGroup.push(
			<ToolbarButton
				key="codeBlock"
				onClick={() => editor.chain().focus().toggleCodeBlock().run()}
				isActive={editor.isActive("codeBlock")}
				title="Code Block"
			>
				<CodeXml size={18} />
			</ToolbarButton>,
		);
	}
	if (blockGroup.length) {
		blockSection = (
			<div className="tiptap-toolbar__group" key="block">
				{blockGroup}
			</div>
		);
	}

	const inlineGroup = [];
	if (has("bold")) {
		inlineGroup.push(
			<ToolbarButton
				key="bold"
				onClick={() => editor.chain().focus().toggleBold().run()}
				isActive={editor.isActive("bold")}
				title="Bold"
			>
				<Bold size={18} />
			</ToolbarButton>,
		);
	}
	if (has("italic")) {
		inlineGroup.push(
			<ToolbarButton
				key="italic"
				onClick={() => editor.chain().focus().toggleItalic().run()}
				isActive={editor.isActive("italic")}
				title="Italic"
			>
				<Italic size={18} />
			</ToolbarButton>,
		);
	}
	if (has("strike")) {
		inlineGroup.push(
			<ToolbarButton
				key="strike"
				onClick={() => editor.chain().focus().toggleStrike().run()}
				isActive={editor.isActive("strike")}
				title="Strikethrough"
			>
				<Strikethrough size={18} />
			</ToolbarButton>,
		);
	}
	if (has("code")) {
		inlineGroup.push(
			<ToolbarButton
				key="code"
				onClick={() => editor.chain().focus().toggleCode().run()}
				isActive={editor.isActive("code")}
				title="Code"
			>
				<Code size={18} />
			</ToolbarButton>,
		);
	}
	if (has("underline")) {
		inlineGroup.push(
			<ToolbarButton
				key="underline"
				onClick={() => editor.chain().focus().toggleUnderline().run()}
				isActive={editor.isActive("underline")}
				title="Underline"
			>
				<UnderlineIcon size={18} />
			</ToolbarButton>,
		);
	}
	if (has("link")) {
		inlineGroup.push(
			<ToolbarButton
				key="link"
				onClick={() => setShowLinkModal(true)}
				isActive={editor.isActive("link")}
				title="Link"
			>
				<LinkIcon size={18} />
			</ToolbarButton>,
		);
	}
	if (has("textColor")) {
		inlineGroup.push(
			<ColorPickerDropdown
				key="textColor"
				editor={editor}
				colors={TEXT_COLORS}
				icon={<Baseline size={18} />}
				title="Kolor tekstu"
				onSelect={(color) => {
					if (color) {
						editor.chain().focus().setColor(color).run();
					} else {
						editor.chain().focus().unsetColor().run();
					}
				}}
			/>,
		);
	}
	if (has("bgColor")) {
		inlineGroup.push(
			<ColorPickerDropdown
				key="bgColor"
				editor={editor}
				colors={BG_COLORS}
				icon={<PaintBucket size={18} />}
				title="Kolor tła"
				onSelect={(color) => {
					if (color) {
						editor.chain().focus().setHighlight({ color }).run();
					} else {
						editor.chain().focus().unsetHighlight().run();
					}
				}}
			/>,
		);
	}

	const inlineSection = [];
	if (inlineGroup.length) {
		inlineSection.push(
			<div className="tiptap-toolbar__group" key="inline">
				{inlineGroup}
			</div>,
		);
	}
	if (has("align")) {
		inlineSection.push(
			<div className="tiptap-toolbar__group" key="align">
				<AlignDropdown editor={editor} />
			</div>,
		);
	}
	if (inlineSection.length) {
		inlineSectionFrag = (
			<Fragment key="inline-section">{inlineSection}</Fragment>
		);
	}

	const mediaSection = [];
	if (has("image")) {
		mediaSection.push(
			<div className="tiptap-toolbar__group" key="image">
				<ToolbarButton
					onClick={() => setShowUploadModal(true)}
					title={t("editor.addImage", "Dodaj zdjęcie")}
				>
					<ImagePlus size={18} />
				</ToolbarButton>
			</div>,
		);
	}
	if (has("callout")) {
		mediaSection.push(
			<div className="tiptap-toolbar__group" key="callout">
				<CalloutDropdown editor={editor} />
			</div>,
		);
	}
	if (has("table")) {
		mediaSection.push(
			<div className="tiptap-toolbar__group" key="table">
				<TableDropdown editor={editor} />
			</div>,
		);
	}
	if (mediaSection.length) {
		mediaSectionFrag = <Fragment key="media-section">{mediaSection}</Fragment>;
	}

	// Kolejność sekcji: dla fiszek najpierw edycja tekstu (inline), potem listy.
	const sections = (
		inlineFirst
			? [historySection, inlineSectionFrag, blockSection, mediaSectionFrag]
			: [historySection, blockSection, inlineSectionFrag, mediaSectionFrag]
	).filter(Boolean);

	return (
		<>
			<ScrollShadowWrapper>
				<div
					className="tiptap-toolbar"
					role="toolbar"
					aria-label="Editor toolbar"
				>
					{label && <span className="tiptap-toolbar__label">{label}</span>}
					{sections.map((section, index) => (
						<Fragment key={index}>
							{index > 0 && <ToolbarSeparator />}
							{section}
						</Fragment>
					))}
					{actions && (
						<>
							<ToolbarSeparator />
							<div className="tiptap-toolbar__group">{actions}</div>
						</>
					)}
				</div>
			</ScrollShadowWrapper>

			{showLinkModal && (
				<TipTapLinkModal
					editor={editor}
					onClose={() => setShowLinkModal(false)}
				/>
			)}

			{showUploadModal && (
				<UploadModal
					mode="photo"
					allowedFormats="jpg, png, svg"
					accept="image/jpeg,image/png,image/svg+xml"
					title={t("editor.addImage", "Dodaj zdjęcie")}
					onConfirm={handleImageConfirm}
					onClose={() => setShowUploadModal(false)}
				/>
			)}
		</>
	);
}

// Akcje na tabeli — współdzielone przez dropdown w belce i inline bubble menu.
export function getTableActions(editor, t) {
	const { hasTitle, hasNote } = getTableFigureParts(editor.state);
	return [
		{
			label: t("editor.table.addColumnAfter", "Dodaj kolumnę"),
			action: () => editor.chain().focus().addColumnAfter().run(),
		},
		{
			label: t("editor.table.deleteColumn", "Usuń kolumnę"),
			action: () => editor.chain().focus().deleteColumn().run(),
		},
		{
			label: t("editor.table.addRowAfter", "Dodaj wiersz"),
			action: () => editor.chain().focus().addRowAfter().run(),
		},
		{
			label: t("editor.table.deleteRow", "Usuń wiersz"),
			action: () => editor.chain().focus().deleteRow().run(),
		},
		{
			label: t("editor.table.toggleHeaderRow", "Nagłówek wiersza"),
			action: () => editor.chain().focus().toggleHeaderRow().run(),
		},
		...(editor.can().toggleTablePart?.("tableTitle")
			? [
					{
						label: hasTitle
							? t("editor.table.removeTitle", "Usuń tytuł tabeli")
							: t("editor.table.addTitle", "Dodaj tytuł tabeli"),
						action: () =>
							editor.chain().focus().toggleTablePart("tableTitle").run(),
					},
					{
						label: hasNote
							? t("editor.table.removeNote", "Usuń legendę")
							: t("editor.table.addNote", "Dodaj legendę"),
						action: () =>
							editor.chain().focus().toggleTablePart("tableNote").run(),
					},
				]
			: []),
		{
			label: t("editor.table.deleteTable", "Usuń tabelę"),
			action: () => editor.chain().focus().deleteTable().run(),
		},
	];
}

function TableDropdown({ editor }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const btnRef = useRef(null);
	const inTable = editor.isActive("table");

	const run = (fn) => {
		fn();
		setOpen(false);
	};

	const items = inTable
		? getTableActions(editor, t)
		: [
				{
					label: t("editor.table.insert", "Wstaw tabelę"),
					action: () =>
						editor
							.chain()
							.focus()
							.insertTable({ rows: 3, cols: 3, withHeaderRow: true })
							.run(),
				},
			];

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<div className="tiptap-toolbar__dropdown-wrapper">
				<button
					ref={btnRef}
					type="button"
					className={`tiptap-toolbar__btn tiptap-toolbar__btn--dropdown${inTable ? " tiptap-toolbar__btn--active" : ""}`}
					onClick={() => setOpen(!open)}
					title={t("editor.table.title", "Tabela")}
				>
					<TableIcon size={18} />
					<ChevronDown size={12} />
				</button>
				<DropdownPortal anchorRef={btnRef} open={open}>
					{items.map((item) => (
						<button
							key={item.label}
							type="button"
							className="tiptap-toolbar__dropdown-item"
							onClick={() => run(item.action)}
						>
							<span>{item.label}</span>
						</button>
					))}
				</DropdownPortal>
			</div>
		</ClickAwayListener>
	);
}

function CalloutDropdown({ editor }) {
	const [open, setOpen] = useState(false);
	const btnRef = useRef(null);
	const isAnyActive = CALLOUT_TYPES.some((type) =>
		editor.isActive("callout", { type }),
	);

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<div className="tiptap-toolbar__dropdown-wrapper">
				<button
					ref={btnRef}
					type="button"
					className={`tiptap-toolbar__btn tiptap-toolbar__btn--dropdown${isAnyActive ? " tiptap-toolbar__btn--active" : ""}`}
					onClick={() => setOpen(!open)}
					title="Callout Box"
				>
					<PanelTop size={18} />
					<ChevronDown size={12} />
				</button>
				<DropdownPortal anchorRef={btnRef} open={open}>
					{CALLOUT_TYPES.map((type) => (
						<button
							key={type}
							type="button"
							className={`tiptap-toolbar__dropdown-item${editor.isActive("callout", { type }) ? " tiptap-toolbar__dropdown-item--active" : ""}`}
							onClick={() => {
								editor.chain().focus().toggleCallout(type).run();
								setOpen(false);
							}}
						>
							<div
								className={`tiptap-callout tiptap-callout--${type}`}
								style={{
									height: "20px",
									minHeight: "20px",
									padding: 0,
									margin: "2px 0",
									width: "100%",
									borderRadius: "4px",
								}}
							/>
						</button>
					))}
				</DropdownPortal>
			</div>
		</ClickAwayListener>
	);
}
